import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useMutation } from '@tanstack/react-query';
import { addDays, format, isEqual, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import SchedulePageComponent from '@/components/SchedulePage/SchedulePageComponent';
import { getScheduledOrderCredits } from '@/components/SchedulePage/helpers/credits';
import useScheduleData from '@/components/SchedulePage/hooks/useScheduleData';
import { useAsyncService } from '@/contexts/AsyncServiceContext';
import { setOptions, setSelectableOptions } from '@/store/ducks/filter';
import { getScheduleDate } from '@/store/selectors/schedule';

import {
  setUnsaved,
  setFilteredInspectors,
  setFilteredPendingOrders,
  setInspectorsAvailabilities,
  addOrUpdateOrder,
  deleteOrder,
  prependPendingOrder,
  addOrderBeingEdited,
  deleteOrderBeingEdited,
  deleteFilteredPendingOrder,
  appendPendingOrders,
} from '../../store/ducks/schedule';
import { showToast } from '../../store/ducks/toasts';

let disabledTimeout;
let emitAssignOrderTimeOut;

const SchedulePageContainer = ({ sidebarCollapsed }) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isDisconnected, setIsDisconnected] = useState(false);
  const [activeView, setActiveView] = useState('schedule');
  const [hasOpenedMap, setHasOpenedMap] = useState(false);

  const dispatch = useDispatch();
  const query = useSelector(state => state.filter.query);
  const selectedOrder = useSelector(state => state.schedule.selectedOrder);
  const orders = useSelector(state => state.schedule.orders);
  const ordersThatAreBeingEdited = useSelector(state => state.schedule.ordersThatAreBeingEdited);
  const unsaved = useSelector(state => state.schedule.unsaved);
  const date = useSelector(getScheduleDate);

  const intl = useIntl();

  const { emit, on, off } = useAsyncService();
  const { ordersLoading, inspectorsLoading, pendingOrdersLoading, pendingOrdersError } = useScheduleData();
  const me = JSON.parse(window.sessionStorage.getItem('me'));
  const totalScheduledCredits = useMemo(
    () => orders.reduce((total, scheduledOrder) => total + getScheduledOrderCredits(scheduledOrder), 0),
    [orders]
  );

  const orderAlreadyBeingEditedByAnotherUser = useMemo(() => {
    const order = ordersThatAreBeingEdited.find(orderThatAreBeingEdited => {
      return (
        orderThatAreBeingEdited?.order?.order?.public_id === selectedOrder?.orderId &&
        orderThatAreBeingEdited?.sender?.id !== me?.uniqueId
      );
    });

    return {
      isEditing: !!order,
      who: order?.sender?.who,
    };
  }, [me, ordersThatAreBeingEdited, selectedOrder]);

  const { mutateAsync: mutateSchedules, isPending: isBusy } = useMutation({
    mutationFn: data => API.post('scheduler/schedules', data),
    onSuccess: () => {
      dispatch(setUnsaved(false));
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({
            id: 'SAVE_CHANGES_AND_INSPECTOR_COMMUNICATED_SUCCESS',
          }),
        })
      );
    },
    onError: () => {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNABLE_SAVE_CHANGES_ERROR' }),
        })
      );
    },
  });

  const handleSubmit = async () => {
    await mutateSchedules({
      date: format(date, 'yyyy-MM-dd'),
      type: 'live',
    });
  };

  const handleViewChange = view => {
    if (view === 'map') setHasOpenedMap(true);

    setActiveView(view);
  };

  useEffect(() => {
    function unsavedChangesMessage(e) {
      if (unsaved) {
        e.preventDefault();
        e.returnValue = intl.formatMessage({ id: 'THERE_ARE_CHANGES_NOT_SENT_TO_DEVICES_MESSAGE' });
        return e.returnValue;
      }
    }

    window.onbeforeunload = unsavedChangesMessage;
    return () => {
      window.onbeforeunload = null;
    };
  }, [intl, unsaved]);

  useEffect(() => {
    async function fetchAvailableFiltersAndSetScheduleOption() {
      try {
        const availableFilters = await API.get('/filters/schedule', {
          params: {
            rule: 'pending_inspections',
          },
        });

        dispatch(
          setSelectableOptions({
            orderTypes: availableFilters.data?.orderTypes || [],
            addresses: availableFilters.data?.addresses || [],
          })
        );

        dispatch(setOptions({ basic: ['text'], advanced: ['schedule'] }));
      } catch (err) {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'FILTER_LOAD_INFORMATION_ERROR' }),
            duration: 5000,
          })
        );
      }
    }

    fetchAvailableFiltersAndSetScheduleOption();
  }, [dispatch, on, intl]);

  useEffect(() => {
    dispatch(
      setFilteredInspectors({
        addresses: query.addresses,
        orderTypes: query.orderTypes,
      })
    );

    dispatch(setInspectorsAvailabilities());

    dispatch(
      setFilteredPendingOrders({
        addresses: query.addresses,
        orderTypes: query.orderTypes,
        text: query.text,
      })
    );
  }, [dispatch, query.addresses, query.orderTypes, query.text]);

  useEffect(() => {
    const handleConnect = () => {
      setIsDisconnected(false);
    };
    const handleDisconnect = () => {
      setIsDisconnected(true);
    };
    const handleSendToTablets = () => {
      setIsButtonDisabled(true);
      disabledTimeout = setTimeout(() => setIsButtonDisabled(false), 10000);
    };
    const handleSendToTabletsFinished = () => {
      setIsButtonDisabled(false);
      dispatch(setUnsaved(false));
      clearTimeout(disabledTimeout);
    };
    const handleOrderIsBeingUpdated = ({ order, sender }) => {
      dispatch(addOrderBeingEdited({ order, sender }));
      emitAssignOrderTimeOut = setTimeout(
        () => dispatch(deleteOrderBeingEdited({ public_id: order?.order?.public_id })),
        10000
      );
    };
    const handleOrderAssigned = ({ order, schedule: scheduleData, sender }) => {
      const parsedCalendarDate = format(date, 'yyyy-MM-dd');
      const parsedOrderScheduleDate = format(addDays(new Date(scheduleData?.when?.date), 1), 'yyyy-MM-dd');

      if (sender?.id !== me.uniqueId) {
        dispatch(deleteFilteredPendingOrder(order?.public_id));

        const isSameDate = isEqual(parseISO(parsedCalendarDate), parseISO(parsedOrderScheduleDate));
        if (sender?.id !== me.uniqueId && isSameDate) {
          dispatch(addOrUpdateOrder({ order, schedule: scheduleData }));
          dispatch(setUnsaved(true));
        }
      }
    };
    const handleOrderEditingFinished = ({ order }) => {
      dispatch(deleteOrderBeingEdited({ public_id: order?.order?.public_id }));
      clearTimeout(emitAssignOrderTimeOut);
    };
    const handleOrderUnassigned = ({ order, sender }) => {
      if (sender?.id === me?.uniqueId) return;

      const orderPublicId = order?.public_id || order?.id;
      dispatch(setUnsaved(true));
      dispatch(deleteOrder(orderPublicId));
      dispatch(deleteOrderBeingEdited({ public_id: orderPublicId }));
      dispatch(
        prependPendingOrder({
          client: order?.client,
          code: order?.code,
          identifier: order?.building_id || order?.identifier,
          id: orderPublicId,
          public_id: orderPublicId,
          type: order?.type,
          credits: order?.credits ?? (order?.price ? Math.round(Number(order.price) * 100) : undefined),
          address_id: order?.address_id,
          order_type_id: order?.order_type_id,
          building_type: order?.building_type,
          allowed_at_date: order?.allowed_at_date,
          accompanied_inspection: order?.accompanied_inspection,
          furnished: order?.furnished,
          modality: order?.modality,
          urgency: order?.urgency,
          created_at: order?.created_at,
          access_information: order?.access_information,
          search_tokens: order?.search_tokens,
          details: order?.details,
          status: order?.status,
          city: order?.city,
          neighborhood: order?.neighborhood,
          street: order?.street,
          number: order?.number,
          complement: order?.complement,
          state: order?.state,
          zipcode: order?.zipcode,
        })
      );
    };
    const handleNewPendingOrder = ({ order }) => {
      dispatch(appendPendingOrders([order]));
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'UPDATE' }),
          text: intl.formatMessage({ id: 'NEW_PENDING_ORDER_ADDED_MESSAGE' }),
          duration: 3000,
        })
      );
    };

    off('connect', handleConnect);
    on('connect', handleConnect);
    off('disconnect', handleDisconnect);
    on('disconnect', handleDisconnect);
    off('SCHEDULE.SEND_TO_TABLETS', handleSendToTablets);
    on('SCHEDULE.SEND_TO_TABLETS', handleSendToTablets);
    off('SCHEDULE.SEND_TO_TABLETS_FINISHED', handleSendToTabletsFinished);
    on('SCHEDULE.SEND_TO_TABLETS_FINISHED', handleSendToTabletsFinished);
    off('SCHEDULE.ORDER_IS_BEING_UPDATED', handleOrderIsBeingUpdated);
    on('SCHEDULE.ORDER_IS_BEING_UPDATED', handleOrderIsBeingUpdated);
    off('SCHEDULE.ORDER_ASSIGNED', handleOrderAssigned);
    on('SCHEDULE.ORDER_ASSIGNED', handleOrderAssigned);
    off('SCHEDULE.ORDER_EDITING_HAS_BEEN_FINISHED', handleOrderEditingFinished);
    on('SCHEDULE.ORDER_EDITING_HAS_BEEN_FINISHED', handleOrderEditingFinished);
    off('SCHEDULE.ORDER_UNASSIGNED', handleOrderUnassigned);
    on('SCHEDULE.ORDER_UNASSIGNED', handleOrderUnassigned);
    off('SCHEDULE.NEW_PENDING_ORDER', handleNewPendingOrder);
    on('SCHEDULE.NEW_PENDING_ORDER', handleNewPendingOrder);

    return () => {
      off('connect', handleConnect);
      off('disconnect', handleDisconnect);
      off('SCHEDULE.SEND_TO_TABLETS', handleSendToTablets);
      off('SCHEDULE.SEND_TO_TABLETS_FINISHED', handleSendToTabletsFinished);
      off('SCHEDULE.ORDER_IS_BEING_UPDATED', handleOrderIsBeingUpdated);
      off('SCHEDULE.ORDER_ASSIGNED', handleOrderAssigned);
      off('SCHEDULE.ORDER_EDITING_HAS_BEEN_FINISHED', handleOrderEditingFinished);
      off('SCHEDULE.ORDER_UNASSIGNED', handleOrderUnassigned);
      off('SCHEDULE.NEW_PENDING_ORDER', handleNewPendingOrder);

      if (emitAssignOrderTimeOut) clearTimeout(emitAssignOrderTimeOut);
      if (disabledTimeout) clearTimeout(disabledTimeout);
    };
  }, [dispatch, on, off, date, me?.uniqueId, intl, unsaved]);

  return (
    <SchedulePageComponent
      isBusy={isBusy}
      isButtonDisabled={isButtonDisabled}
      isDisconnected={isDisconnected}
      selectedOrder={selectedOrder}
      totalScheduledCredits={totalScheduledCredits}
      handleSubmit={handleSubmit}
      intl={intl}
      orderAlreadyBeingEditedByAnotherUser={orderAlreadyBeingEditedByAnotherUser}
      isUnsaved={unsaved}
      sidebarCollapsed={sidebarCollapsed}
      activeView={activeView}
      hasOpenedMap={hasOpenedMap}
      onViewChange={handleViewChange}
      ordersLoading={ordersLoading}
      inspectorsLoading={inspectorsLoading}
      pendingOrdersLoading={pendingOrdersLoading}
      pendingOrdersError={pendingOrdersError}
    />
  );
};
SchedulePageContainer.propTypes = {
  sidebarCollapsed: PropTypes.bool.isRequired,
};
export default SchedulePageContainer;
